import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  Tooltip,
  Skeleton,
  Chip,
  Collapse,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Psychology as AnalysisIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckIcon,
  AttachMoney as MoneyIcon,
  People as PeopleIcon,
  Google as GoogleIcon,
  Facebook as FacebookIcon,
  MusicNote as TikTokIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const AIReasoningDisplay = ({ reasoning, aiData, isLoading, onUpdate }) => {
  const [copied, setCopied] = useState(false);
  const [displayText, setDisplayText] = useState('');
  const [expanded, setExpanded] = useState(false);
  
  // Typewriter effect for summary
  useEffect(() => {
    const summaryText = reasoning?.budget_rationale || reasoning?.platform_allocation || '';
    if (summaryText && !isLoading) {
      let currentIndex = 0;
      setDisplayText('');
      
      const interval = setInterval(() => {
        if (currentIndex <= summaryText.length) {
          setDisplayText(summaryText.substring(0, currentIndex));
          currentIndex += 3;
        } else {
          clearInterval(interval);
          setDisplayText(summaryText);
        }
      }, 10);
      
      return () => clearInterval(interval);
    }
  }, [reasoning?.budget_rationale, reasoning?.platform_allocation, isLoading]);
  
  const handleCopy = () => {
    if (reasoning) {
      const fullText = `
AI Reasoning Summary:
${reasoning?.budget_rationale || reasoning?.platform_allocation || ''}

Budget Allocation:
- Total: $${aiData?.budget?.total || 0}
- Google Ads: $${aiData?.budget?.google_ads || 0}
- Meta: $${aiData?.budget?.meta || 0}
- TikTok: $${aiData?.budget?.tiktok || 0}

Platform Strategy:
${reasoning?.platform_allocation || ''}

Key Assumptions:
${reasoning?.assumptions?.join('\n') || ''}

Recommendations:
${reasoning?.recommendations?.join('\n') || ''}
      `;
      navigator.clipboard.writeText(fullText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };
  
  if (!reasoning && !isLoading) return null;
  
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Card
        sx={{
          background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1), rgba(139, 92, 246, 0.1))',
          backdropFilter: 'blur(10px)',
          border: '1px solid',
          borderColor: 'primary.dark',
          position: 'relative',
          overflow: 'visible'
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: -12,
            left: 24,
            px: 2,
            py: 0.5,
            background: 'linear-gradient(135deg, #6366F1, #8B5CF6)',
            borderRadius: 2
          }}
        >
          <Typography variant="caption" sx={{ color: 'white', fontWeight: 600 }}>
            AI Reasoning & Decisions
          </Typography>
        </Box>
        
        <CardContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <AnalysisIcon sx={{ color: 'primary.main', mt: 2.5 }} />
            
            <Box sx={{ flex: 1 }}>
              {!isLoading && reasoning && (
                <>
                  {/* Budget Allocation - Moved to top and made more prominent */}
                  <Box sx={{ mb: 3, mt: 2, p: 2, bgcolor: 'rgba(76, 175, 80, 0.1)', borderRadius: 2, border: '1px solid rgba(76, 175, 80, 0.3)' }}>
                    <Typography variant="h6" sx={{ color: '#4CAF50', fontWeight: 600, mb: 1.5 }}>
                      Recommended Budget Allocation
                    </Typography>
                    <Typography variant="h5" sx={{ color: 'text.primary', fontWeight: 700, mb: 2 }}>
                      Total: ${aiData?.budget?.total || 0}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip
                        icon={<GoogleIcon sx={{ color: 'white !important' }} />}
                        label={
                          <span>
                            <span style={{ color: 'white' }}>Google: </span>
                            <span style={{ color: '#4CAF50', fontWeight: 700 }}>${aiData?.budget?.google_ads || 0}</span>
                          </span>
                        }
                        size="small"
                        sx={{
                          bgcolor: 'rgba(76, 175, 80, 0.2)',
                          fontWeight: 600
                        }}
                      />
                      <Chip
                        icon={<FacebookIcon sx={{ color: 'white !important' }} />}
                        label={
                          <span>
                            <span style={{ color: 'white' }}>Meta: </span>
                            <span style={{ color: '#4CAF50', fontWeight: 700 }}>${aiData?.budget?.meta || 0}</span>
                          </span>
                        }
                        size="small"
                        sx={{
                          bgcolor: 'rgba(76, 175, 80, 0.2)',
                          fontWeight: 600
                        }}
                      />
                      <Chip
                        icon={<TikTokIcon sx={{ color: 'white !important' }} />}
                        label={
                          <span>
                            <span style={{ color: 'white' }}>TikTok: </span>
                            <span style={{ color: '#4CAF50', fontWeight: 700 }}>${aiData?.budget?.tiktok || 0}</span>
                          </span>
                        }
                        size="small"
                        sx={{
                          bgcolor: 'rgba(76, 175, 80, 0.2)',
                          fontWeight: 600
                        }}
                      />
                    </Box>
                  </Box>
                </>
              )}
              
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Campaign Strategy Summary:
              </Typography>
              
              {isLoading ? (
                <>
                  <Skeleton variant="text" width="80%" />
                  <Skeleton variant="text" width="60%" />
                </>
              ) : (
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontFamily: 'monospace',
                    color: 'primary.light',
                    minHeight: '2em',
                    mb: 2
                  }}
                >
                  {displayText}
                  {reasoning?.summary && displayText.length < reasoning.summary.length && (
                    <span style={{ opacity: 0.5 }}>|</span>
                  )}
                </Typography>
              )}
              
              {!isLoading && reasoning && (
                <>
                  
                  {/* Expandable Details */}
                  <Box>
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        cursor: 'pointer',
                        mb: 1
                      }}
                      onClick={() => setExpanded(!expanded)}
                    >
                      <Typography variant="subtitle2" color="text.secondary">
                        Detailed Reasoning
                      </Typography>
                      <IconButton size="small">
                        {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </Box>
                    
                    <Collapse in={expanded}>
                      <List dense>
                        {reasoning?.platform_allocation && (
                          <ListItem>
                            <ListItemIcon>
                              <MoneyIcon fontSize="small" sx={{ color: 'primary.main' }} />
                            </ListItemIcon>
                            <ListItemText 
                              primary="Platform Strategy"
                              secondary={reasoning.platform_allocation}
                            />
                          </ListItem>
                        )}
                        
                        {reasoning?.budget_rationale && (
                          <ListItem>
                            <ListItemIcon>
                              <MoneyIcon fontSize="small" sx={{ color: 'success.main' }} />
                            </ListItemIcon>
                            <ListItemText 
                              primary="Budget Rationale"
                              secondary={reasoning.budget_rationale}
                            />
                          </ListItem>
                        )}
                        
                        {reasoning?.success_metrics && reasoning.success_metrics.length > 0 && (
                          <ListItem>
                            <ListItemIcon>
                              <CheckIcon fontSize="small" sx={{ color: 'info.main' }} />
                            </ListItemIcon>
                            <ListItemText 
                              primary="Success Metrics"
                              secondary={
                                <Box component="span" sx={{ display: 'block', m: 0, pl: 2 }}>
                                  {reasoning.success_metrics.map((metric, idx) => (
                                    <Box
                                      component="span"
                                      key={idx}
                                      sx={{ display: 'block', mb: 0.5 }}
                                    >
                                      • {metric}
                                    </Box>
                                  ))}
                                </Box>
                              }
                              secondaryTypographyProps={{
                                component: 'div'
                              }}
                            />
                          </ListItem>
                        )}
                        
                        {reasoning?.recommendations && reasoning.recommendations.length > 0 && (
                          <ListItem>
                            <ListItemIcon>
                              <CheckIcon fontSize="small" sx={{ color: 'warning.main' }} />
                            </ListItemIcon>
                            <ListItemText 
                              primary="Recommendations"
                              secondary={
                                <Box component="span" sx={{ display: 'block', m: 0, pl: 2 }}>
                                  {reasoning.recommendations.map((rec, idx) => (
                                    <Box
                                      component="span"
                                      key={idx}
                                      sx={{ display: 'block', mb: 0.5 }}
                                    >
                                      • {rec}
                                    </Box>
                                  ))}
                                </Box>
                              }
                              secondaryTypographyProps={{
                                component: 'div'
                              }}
                            />
                          </ListItem>
                        )}
                        
                        {aiData?.target_audience && (
                          <ListItem>
                            <ListItemIcon>
                              <PeopleIcon fontSize="small" sx={{ color: 'primary.main' }} />
                            </ListItemIcon>
                            <ListItemText 
                              primary="Target Audience"
                              secondary={
                                <Box component="span" sx={{ display: 'block', m: 0 }}>
                                  {aiData.target_audience.demographics && (
                                    <Box sx={{ mb: 1 }}>
                                      <strong>Demographics:</strong> Age {aiData.target_audience.demographics.age_range}, {aiData.target_audience.demographics.gender}, {aiData.target_audience.demographics.income_level}
                                    </Box>
                                  )}
                                  {aiData.target_audience.psychographics && (
                                    <Box>
                                      <strong>Interests:</strong> {aiData.target_audience.psychographics.interests?.join(', ')}
                                    </Box>
                                  )}
                                </Box>
                              }
                              secondaryTypographyProps={{
                                component: 'div'
                              }}
                            />
                          </ListItem>
                        )}
                        
                        {reasoning.assumptions && reasoning.assumptions.length > 0 && (
                          <ListItem>
                            <ListItemIcon>
                              <CheckIcon fontSize="small" sx={{ color: 'success.main' }} />
                            </ListItemIcon>
                            <ListItemText 
                              primary="Key Assumptions"
                              secondary={
                                <Box component="span" sx={{ display: 'block', m: 0, pl: 2 }}>
                                  {reasoning.assumptions.map((assumption, idx) => (
                                    <Box
                                      component="span"
                                      key={idx}
                                      sx={{ display: 'block', mb: 0.5 }}
                                    >
                                      • {assumption}
                                    </Box>
                                  ))}
                                </Box>
                              }
                              secondaryTypographyProps={{
                                component: 'div'
                              }}
                            />
                          </ListItem>
                        )}
                      </List>
                    </Collapse>
                  </Box>
                </>
              )}
            </Box>
            
            {!isLoading && reasoning && (
              <Tooltip title={copied ? 'Copied!' : 'Copy reasoning'}>
                <IconButton
                  onClick={handleCopy}
                  size="small"
                  sx={{
                    color: copied ? 'success.main' : 'text.secondary',
                    transition: 'color 0.3s'
                  }}
                >
                  <CopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          
          {!isLoading && reasoning && (
            <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
              <Chip
                label="Claude Sonnet 4"
                size="small"
                sx={{
                  bgcolor: 'rgba(99, 102, 241, 0.2)',
                  color: 'primary.light',
                  fontSize: '0.75rem'
                }}
              />
              <Chip
                label="AWS Bedrock"
                size="small"
                sx={{
                  bgcolor: 'rgba(245, 158, 11, 0.2)',
                  color: 'warning.light',
                  fontSize: '0.75rem'
                }}
              />
            </Box>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default AIReasoningDisplay;